import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { RootState } from "redux/store";
import {
	getAgentsWithLeads,
	getLeadsPageFilters,
	getLeadsPageFiltersCount,
	getLeadsPageLeadIds,
	getLeadsPagePageSize,
	getLeadsPageSearchTerm,
	getLeadsPageSort,
} from "redux/selector";
import { updateLeadsPagePage } from "redux/slice/leads-page";
import { FiltersChips } from "component/page/leads/filter-dialog/filter-criteria-chips";
import { urls } from "routes/urls";
import { getLeadNavigationPosition, LeadNavigationLocationState } from "shared/lead-navigation";
import { getLeadsPageSortLabel } from "shared/lead-list";
import { styles } from "./style";

interface Props extends WithStyles<typeof styles> {
	leadId: number;
}

const LeadNavigationBarComponent: React.FC<Props> = (props) => {
	const { leadId, classes = {} } = props;

	const history = Router.useHistory();
	const location = Router.useLocation<LeadNavigationLocationState | undefined>();
	const dispatch = useDispatch<any>();

	const leadIds = useSelector((state: RootState) => getLeadsPageLeadIds(state));
	const filters = useSelector((state: RootState) => getLeadsPageFilters(state));
	const filterCount = useSelector((state: RootState) => getLeadsPageFiltersCount(state));
	const sort = useSelector((state: RootState) => getLeadsPageSort(state));
	const term = useSelector((state: RootState) => getLeadsPageSearchTerm(state));
	const pageSize = useSelector((state: RootState) => getLeadsPagePageSize(state));
	const agents = useSelector((state: RootState) => getAgentsWithLeads(state));

	const [summaryAnchorEl, setSummaryAnchorEl] = useState<HTMLElement | null>(null);

	// hidden when opened outside the Lead List, once the lead no longer matches its filters, or
	// when the search it was opened with is gone (a reload clears it)
	const inLeadListFlow =
		!!location.state?.fromLeadList && (location.state.leadListTerm ?? "") === term;
	const position = inLeadListFlow ? getLeadNavigationPosition(leadIds, leadId) : undefined;

	const goToLead = useCallback(
		(id: number) => {
			const state: LeadNavigationLocationState = { fromLeadList: true, leadListTerm: term };
			history.push(urls.lead(id), state);
		},
		[history, term]
	);

	const goToLeadList = useCallback(() => {
		if (!position) {
			return;
		}
		dispatch(updateLeadsPagePage(Math.floor(position.index / pageSize)));
		const state: LeadNavigationLocationState = {
			leadsPageReturn: { leadId },
		};
		history.push(urls.leads({ leadsPageFilters: filters, sort }), state);
	}, [dispatch, history, position, pageSize, leadId, filters, sort]);

	if (!position) {
		return null;
	}

	const { previousLeadId, nextLeadId } = position;

	return (
		<div className={classes.bar}>
			<Mui.Link component="button" onClick={goToLeadList} className={classes.backLink}>
				<FeatherIcon className={classes.icon}>
					<Icons.ArrowLeft />
				</FeatherIcon>
				Back to Lead List
			</Mui.Link>
			<div className={classes.controls}>
				<Mui.Button
					variant="outlined"
					onClick={(event) => setSummaryAnchorEl(event.currentTarget)}
					aria-label="Filters and sort"
					startIcon={
						<FeatherIcon className={classes.icon}>
							<Icons.Filter />
						</FeatherIcon>
					}
					endIcon={
						<FeatherIcon className={classes.icon}>
							<Icons.ChevronDown />
						</FeatherIcon>
					}
					className={classes.filterButton}
				>
					{`(${filterCount})`}
				</Mui.Button>
				<Mui.Popover
					open={!!summaryAnchorEl}
					anchorEl={summaryAnchorEl}
					onClose={() => setSummaryAnchorEl(null)}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
				>
					<div className={classes.summary}>
						<Mui.Typography variant="h4">Filters & Sort</Mui.Typography>
						{term && (
							<div className={classes.summaryRow}>
								<Mui.Typography className={classes.summaryLabel}>Search</Mui.Typography>
								<Mui.Typography>{`"${term}"`}</Mui.Typography>
							</div>
						)}
						<div className={classes.summaryRow}>
							<Mui.Typography className={classes.summaryLabel}>Filters</Mui.Typography>
							{filterCount ? (
								<div className={classes.chips}>
									<FiltersChips agents={agents} readOnly />
								</div>
							) : (
								<Mui.Typography>None</Mui.Typography>
							)}
						</div>
						<div className={classes.summaryRow}>
							<Mui.Typography className={classes.summaryLabel}>Sort</Mui.Typography>
							<Mui.Typography>
								{`${getLeadsPageSortLabel(sort)}, ${sort.desc ? "descending" : "ascending"}`}
							</Mui.Typography>
						</div>
						<div className={classes.summaryRow}>
							<Mui.Typography className={classes.summaryLabel}>Leads</Mui.Typography>
							<Mui.Typography>{position.total.toLocaleString()}</Mui.Typography>
						</div>
						<Mui.Link component="button" onClick={goToLeadList} className={classes.summaryLink}>
							View in Lead List
						</Mui.Link>
					</div>
				</Mui.Popover>
				<div className={classes.pager}>
					<Mui.Button
						variant="text"
						disabled={previousLeadId === undefined}
						onClick={() => previousLeadId !== undefined && goToLead(previousLeadId)}
						startIcon={
							<FeatherIcon className={classes.icon}>
								<Icons.ChevronLeft />
							</FeatherIcon>
						}
						className={classes.pagerButton}
					>
						Previous
					</Mui.Button>
					<Mui.Typography className={classes.position}>
						{`Lead ${(position.index + 1).toLocaleString()} of ${position.total.toLocaleString()}`}
					</Mui.Typography>
					<Mui.Button
						variant="text"
						disabled={nextLeadId === undefined}
						onClick={() => nextLeadId !== undefined && goToLead(nextLeadId)}
						endIcon={
							<FeatherIcon className={classes.icon}>
								<Icons.ChevronRight />
							</FeatherIcon>
						}
						className={classes.pagerButton}
					>
						Next
					</Mui.Button>
				</div>
			</div>
		</div>
	);
};

export const LeadNavigationBar = withStyles(LeadNavigationBarComponent, styles);
